import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { BranchDto, WarehouseInput, WarehouseRecordDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { Confirm, Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton, lockTag } from "./common";
import { AccName, accountPairs, AccountSelect, accText, useAccountOptions } from "../accounting/common";
import { amt } from "../ledger/common";

// Warehouses › All Warehouses, ported from the prototype (erp-warehouses.js "/warehouses/all"). Audited columns:
// names, branch and account index; the New Warehouse dialog has branch, account index and names. The account
// index is an optional level-3 account (evidence on whether it is required is pending). The prototype's stock
// columns, items in stock and stock value, come from the stock ledger (phase 8).

function WarehouseForm({ wh, branches, defaultBranch, onClose, onSaved }: { wh: WarehouseRecordDto | null; branches: BranchDto[]; defaultBranch: string; onClose(): void; onSaved(w: WarehouseRecordDto, created: boolean): void }) {
  const { t, L } = useLang();
  const { opts, failed } = useAccountOptions();
  const [v, setV] = useState<WarehouseInput>(() => wh ? { branchId: wh.branchId, nameAr: wh.nameAr, nameEn: wh.nameEn, accountId: wh.accountId } : { branchId: defaultBranch, nameAr: "", nameEn: "", accountId: null });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof WarehouseInput, x: string | null) => { setV((o) => ({ ...o, [k]: x })); setErrs((e) => ({ ...e, [k]: "" })); };
  async function save() {
    const e: Record<string, string> = {};
    if (!v.branchId) e.branchId = t("requiredField");
    if (!v.nameAr.trim()) e.nameAr = t("requiredField");
    if (!v.nameEn.trim()) e.nameEn = t("requiredField");
    setErrs(e);
    if (Object.keys(e).length) { setAlert(t("fixRequired")); return; }
    setBusy(true); setAlert(null);
    try { onSaved(wh ? await api<WarehouseRecordDto>("PUT", `/settings/warehouses/${wh.id}`, v) : await api<WarehouseRecordDto>("POST", "/settings/warehouses", v), !wh); }
    catch (err) { setErrs(fieldErrorsOf(t, err)); setAlert(errorText(t, err)); }
    finally { setBusy(false); }
  }
  return (
    <Modal title={t(wh ? "editWarehouse" : "newWarehouse")} onClose={onClose} onSubmit={save} alert={alert} busy={busy}>
      <Field label={t("branch")} req error={errs.branchId} htmlFor="ff-branch">
        <select id="ff-branch" className="control" value={v.branchId} onChange={(e) => set("branchId", e.target.value)}>
          <option value="">{t("choose")}</option>{branches.map((b) => <option key={b.id} value={b.id}>{L(b)}</option>)}
        </select>
      </Field>
      <Field label={t("accountIndex")} error={errs.accountId} htmlFor="ff-acc"><AccountSelect id="ff-acc" value={v.accountId ?? ""} onChange={(x) => set("accountId", x || null)} opts={opts} failed={failed} empty={t("noAccount")} /></Field>
      <Field label={t("nameAr")} req error={errs.nameAr} htmlFor="ff-nameAr"><input id="ff-nameAr" className="control" value={v.nameAr} onChange={(e) => set("nameAr", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("nameEn")} req error={errs.nameEn} htmlFor="ff-nameEn"><input id="ff-nameEn" className="control" value={v.nameEn} onChange={(e) => set("nameEn", e.target.value)} autoComplete="off" /></Field>
    </Modal>
  );
}

export function Warehouses() {
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const nav = useNavigate();
  const [rows, setRows] = useState<WarehouseRecordDto[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<WarehouseRecordDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<WarehouseRecordDto | null>(null);
  const accounts = useAccountOptions();
  // The top bar's branch picker narrows the list, as in the prototype; the custom search can narrow it further.
  const branch = filters.branch || (s.branch !== "all" ? s.branch : "");

  const load = useCallback(async () => {
    setFailed(null);
    const p = new URLSearchParams();
    if (filters.name) p.set("q", filters.name);
    if (branch) p.set("branchId", branch);
    if (filters.acc) p.set("accountId", filters.acc);
    try { setRows(await api<WarehouseRecordDto[]>("GET", `/settings/warehouses${p.size ? "?" + p : ""}`)); }
    catch (e) { setFailed(errorText(t, e)); }
  }, [filters.name, filters.acc, branch, t]);
  useEffect(() => { void load(); }, [load]);

  async function doDelete(w: WarehouseRecordDto) {
    setDeleting(null);
    try { await api("DELETE", `/settings/warehouses/${w.id}`); toast(t("deletedOk", { x: L(w) })); await load(); }
    catch (e) { toast(errorText(t, e)); }
  }
  const brName = (x: { branchNameEn: string; branchNameAr: string }) => L({ nameEn: x.branchNameEn, nameAr: x.branchNameAr });
  const columns: Column<WarehouseRecordDto>[] = [
    { k: "ar", label: t("nameAr"), cell: (w) => <span lang="ar" className="cell-name">{w.nameAr}</span>, csv: (w) => w.nameAr },
    { k: "en", label: t("nameEn"), cell: (w) => <span lang="en">{w.nameEn}</span>, csv: (w) => w.nameEn },
    { k: "branch", label: t("branch"), cell: (w) => brName(w), csv: (w) => brName(w) },
    { k: "acc", label: t("accountIndex"), cell: (w) => <AccName a={w.account} />, csv: (w) => accText(L, w.account) },
    { k: "items", label: t("itemsInStock"), end: true, cell: (w) => (s.can("warehouses") ? <Link to={`/warehouse-stock?wh=${w.id}`}>{w.itemsInStock}</Link> : w.itemsInStock), csv: (w) => w.itemsInStock },
    { k: "value", label: t("stockValue"), end: true, cell: (w) => amt(w.stockValue), csv: (w) => amt(w.stockValue) }
  ];
  const newBtn = s.can("warehouseAdd") ? <button className="btn btn-primary" onClick={() => setEditing("new")}><Icon name="plus" />{t("newWarehouse")}</button>
    : <LockedButton label={t("newWarehouse")} title={t("noPermissionFor", { p: "warehouseAdd" })} />;

  return (
    <Shell navKey="/warehouses/all">
      <PageHead crumbs={[[t("home"), "/home"], [t("warehouses")], [t("allWarehouses")]]} title={t("allWarehouses")} />
      <ListView rows={rows} error={failed} columns={columns} file="warehouses" testId="warehouses-table" onRefresh={() => void load()}
        search={[
          { k: "name", label: t("warehouse"), type: "text" },
          { k: "branch", label: t("branch"), type: "select", options: s.branches.map((b) => [b.id, L(b)]) },
          { k: "acc", label: t("accountIndex"), type: "select", options: accountPairs(L, accounts.opts) }
        ]} filters={filters} onFilters={setFilters}
        quick={(w) => `${w.nameEn} ${w.nameAr} ${brName(w)}`} newButton={newBtn}
        empty={{ icon: "warehouse", title: t("whEmpty"), body: t("whEmptyBody") }}
        actions={(w) => [
          { label: t("edit"), icon: "edit", run: () => setEditing(w), ...(s.can("warehouseUpdate") ? {} : lockTag(t)) },
          { label: t("viewStock"), icon: "box", run: () => nav(`/warehouse-stock?wh=${w.id}`) },
          { label: t("newBeginningHere"), icon: "plus", run: () => nav(`/invoices/create?type=BeginningInventory&wh=${w.id}`), ...(s.can("invoiceBeginningInventoryAdd") ? {} : lockTag(t)) },
          { label: t("newCountHere"), icon: "check", run: () => nav(`/warehouses/inventory/new?type=Inventory&wh=${w.id}`), ...(s.can("invoiceInventoryAdd") ? {} : lockTag(t)) },
          { label: "", sep: true },
          { label: t("delete"), icon: "trash", danger: true, run: () => setDeleting(w), ...(s.can("warehouseDelete") ? {} : lockTag(t)) }
        ]} />
      {editing && <WarehouseForm wh={editing === "new" ? null : editing} branches={s.branches} defaultBranch={s.branch !== "all" ? s.branch : s.me?.scope.branchId ?? s.branches[0]?.id ?? ""}
        onClose={() => setEditing(null)} onSaved={(w, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: L(w) })); void load(); void s.refresh(); }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => void doDelete(deleting)} />}
    </Shell>
  );
}
